import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

interface IEntityPaneHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailingContent?: React.ReactNode;
  avatar?: React.ReactNode;
  /**
   * Optional row rendered inside the header, below the title row (e.g. a tab
   * bar). Because it lives inside the header, it stays pinned with the title
   * and the scroll-driven drop shadow falls below it instead of between it and
   * the title.
   */
  belowContent?: React.ReactNode;
  className?: string;
}

/**
 * Title row that sits above EntityPaneBody, against the top edge of the pane
 * card `HomeScene` seats every routed pane on. Mirrors the Figma "Section
 * Header" component: title in `text-xl` semibold on mobile and
 * `text-display-xs` semibold at md+. At lg+ the top inset is a fixed 8px
 * (`lg:pt-2`), seating the title on the same line as the sidebar wordmark and
 * the assistant panel's title; it does not shrink on scroll — with an 8px
 * inset there is nothing left to shed.
 *
 * Once the pane scroller passes the scroll threshold (16px), a drop shadow
 * appears below the header at every viewport — darker below lg, much lighter
 * at lg+ (per Figma) — driven by `--entity-header-scroll-progress` (binary
 * 0 | 1), set by the scroller in HomeScene (see useHeaderScrollProgress).
 *
 * The avatar+text wrapper is `items-start` so the title's line stays fixed
 * whether or not an avatar (or a wrapping subtitle) rides along — an
 * avatar-carrying header's title sits on the same line as an avatar-less
 * one's, per the Figma Account header (avatar and text top-aligned). The
 * outer flex stays `items-center` so the trailing actions cluster vertically
 * centers against the avatar+text block.
 *
 * `sticky top-0` pins the header at the top of the pane scroller while the body
 * scrolls beneath it; `entity-header-surface` keeps body content from showing
 * through (a 99.5%-opaque color-mix, so the compositor never occlusion-culls
 * content under the header; see styles/app.css), and `z-10` raises it (and its
 * drop shadow) above the scrolling cards.
 *
 * `belowContent` (e.g. a tab bar) renders inside the header below the title
 * row, so it pins with the header and the drop shadow falls below it.
 */
const EntityPaneHeader = ({
  title,
  subtitle,
  trailingContent,
  avatar,
  belowContent,
  className,
}: IEntityPaneHeaderProps) => {
  return (
    <div
      className={cx(
        'entity-header-scroll-shadow entity-header-surface sticky top-0 z-10 flex w-full flex-col px-5 pt-5 lg:pt-2',
        // belowContent (e.g. a tab bar) owns the header's bottom edge so its
        // underline sits flush against the scroll shadow — drop the gap.
        belowContent ? 'pb-0' : 'pb-2 md:pb-3',
        className
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {avatar && <div className="flex shrink-0 items-start">{avatar}</div>}
          <div className="flex min-w-0 flex-col">
            <div className="text-primary md:text-display-xs truncate text-xl font-semibold">{title}</div>
            {subtitle && <div className="text-tertiary text-sm font-normal">{subtitle}</div>}
          </div>
        </div>
        {trailingContent && <div className="text-secondary flex shrink-0 text-sm">{trailingContent}</div>}
      </div>
      {belowContent && <div className="mt-5">{belowContent}</div>}
    </div>
  );
};

export default EntityPaneHeader;
